import { useEffect } from 'react';
import { App, Form, Input, Modal, Switch } from 'antd';
import { useCreateUser, useUpdateUser } from '@/hooks/useUsers';
import type { UserDto } from '@/types/user';

interface Props {
  open: boolean;
  user: UserDto | null;
  onClose: () => void;
}

interface FormValues {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  isSuperAdmin: boolean;
}

export function UserFormModal({ open, user, onClose }: Props) {
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const isEdit = user != null;

  const createMut = useCreateUser();
  const updateMut = useUpdateUser();

  useEffect(() => {
    if (open) {
      if (user) {
        form.setFieldsValue({
          email: user.email,
          password: '',
          firstName: user.firstName,
          lastName: user.lastName,
          isSuperAdmin: user.isSuperAdmin,
        });
      } else {
        form.resetFields();
        form.setFieldsValue({ isSuperAdmin: false });
      }
    }
  }, [open, user, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      if (isEdit && user) {
        await updateMut.mutateAsync({
          id: user.id,
          request: {
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            isSuperAdmin: values.isSuperAdmin,
          },
        });
        message.success('Usuario actualizado.');
      } else {
        await createMut.mutateAsync({
          email: values.email.trim().toLowerCase(),
          password: values.password,
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          isSuperAdmin: values.isSuperAdmin,
        });
        message.success('Usuario creado.');
      }

      onClose();
    } catch (err: any) {
      if (err?.errorFields) return; // validacion del form
      // interceptor muestra error de api
    }
  };

  return (
    <Modal
      title={isEdit ? `Editar usuario: ${user?.fullName}` : 'Nuevo usuario'}
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      okText={isEdit ? 'Guardar cambios' : 'Crear'}
      cancelText="Cancelar"
      confirmLoading={createMut.isPending || updateMut.isPending}
      destroyOnClose
      width={520}
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          label="Email"
          name="email"
          rules={[
            { required: true, message: 'Email requerido.' },
            { type: 'email', message: 'Email invalido.' },
          ]}
        >
          <Input placeholder="usuario@empresa.com" disabled={isEdit} />
        </Form.Item>

        {!isEdit && (
          <Form.Item
            label="Password inicial"
            name="password"
            rules={[
              { required: true, message: 'Password requerida.' },
              { min: 8, message: 'Minimo 8 caracteres.' },
            ]}
            extra="El usuario podra cambiarla en su primer login."
          >
            <Input.Password placeholder="Minimo 8 caracteres" />
          </Form.Item>
        )}

        <Form.Item
          label="Nombre"
          name="firstName"
          rules={[{ required: true, message: 'Nombre requerido.' }]}
        >
          <Input maxLength={100} />
        </Form.Item>

        <Form.Item
          label="Apellido"
          name="lastName"
          rules={[{ required: true, message: 'Apellido requerido.' }]}
        >
          <Input maxLength={100} />
        </Form.Item>

        <Form.Item
          label="Super Admin"
          name="isSuperAdmin"
          valuePropName="checked"
          extra="Acceso total a todas las Enterprises del sistema."
        >
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
}
